import React from 'react';
import * as styles from './styles.css';
import Text from '../common/text';
import { atom } from 'jotai/vanilla';
import { useAtomValue } from 'jotai/react';
import {
  IAvailability,
  IComponentModel,
  IContactInfo,
  IEducation,
  IOption,
  ISection,
  ISectionFrame,
} from '../../interfaces';
import { json_parser } from '../../atoms/helpers';
import SectionHeader from './section-header';
import SectionBody from './section-body';
import { Checkbox } from '@mui/material';
import { useProps } from '../../hooks/use-props';
import atoms from '../../atoms';
import actions, { ActionTypes } from '../../atoms/actions';

export interface IReviewProps {
  id: string;
}

export interface IReviewSectionItem {
  question: string;
  answer: string;
}

const answer_parsers = {
  information: (answer: string) => {
    const contact_info: IContactInfo = JSON.parse(answer);
    return [
      { question: 'First Name', answer: contact_info.first_name },
      { question: 'Last Name', answer: contact_info.last_name },
      { question: 'Email Address', answer: contact_info.email },
      { question: 'Phone Number', answer: contact_info.phone_number },
      { question: 'Postal/ZIP Code', answer: contact_info.zipcode },
    ];
  },
  education: (answer: string) => {
    const all_answers: IEducation[] = json_parser(answer);
    return all_answers.reduce<Record<string, unknown>[]>((acc, education) => {
      const result = [
        ...acc,
        { question: 'University/College', answer: education.school },
        { question: 'Degree', answer: education.degree },
        { question: 'Major', answer: education.major },
        { question: 'Graduation Year (or expected)', answer: education.graduation_year },
      ];

      result.push({
        question: 'GPA (approximate)',
        answer: education.gpa ? education.gpa.toString() : 'N/A',
      });

      return result;
    }, []);
  },
  availability: (answer_string: string) => {
    const availability: IAvailability = JSON.parse(answer_string);
    return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].reduce(
      (acc, key) => {
        if (!Object.keys(availability).includes(key)) return acc;
        const day = availability[key];
        const periods = ['morning', 'afternoon', 'evening'].reduce<string>((acc, period) => {
          if (!Object.keys(day).includes(period)) return acc;
          const is_valid_day = day[period] === true;
          if (is_valid_day) {
            const is_acc_empty = acc === '';
            if (is_acc_empty) return period;
            return `${acc}, &nbsp;${period}`;
          }
          return acc;
        }, '');
        if (periods.length === 0) return acc;
        return [...acc, `**${key}:** &nbsp;${periods}`];
      },
      [] as string[],
    );
  },
  options: (answer_string: string, options: IOption[]) => {
    const parsed_answer = json_parser(answer_string);
    const answer_array = Array.isArray(parsed_answer) ? parsed_answer : [parsed_answer];
    return answer_array.reduce((acc, answer) => {
      const option = options.find(({ id }) => id === answer);
      if (!option) return acc;
      return [...acc, option.label];
    }, []);
  },
};

const review = atom((get) =>
  (get(atoms.frame.sections) as ISection[]).map(({ id, name, frames }) => {
    const subjects = get(actions[ActionTypes.subjects].data) as { options?: IOption[] };
    const states = get(actions[ActionTypes.states].data) as { options?: IOption[] };

    // Gets all components for all frames in the section
    const components = (frames as ISectionFrame[]).reduce<IComponentModel[]>(
      (acc, { components }) => [...acc, ...components],
      [],
    );
    const questions = (components as IComponentModel[]).reduce<IReviewSectionItem[]>(
      (acc, component) => {
        const { type, answer, options, label, action } = component;
        let opts = options;

        if (action === 'subjects') {
          opts = subjects?.options;
        }
        if (action === 'states') {
          opts = states?.options;
        }
        if (action === 'preferred_subject') {
          opts = subjects?.options;
        }

        if (!answer) return acc;
        if (Object.keys(answer_parsers).includes(type as string)) {
          if (type === 'availability')
            return [
              ...acc,
              {
                question: "What's your general availability?",
                answer: answer_parsers.availability(answer as string),
              },
            ];
          if (type === 'education') {
            const questions = answer_parsers.education(answer as string);
            return [...acc, ...questions];
          }
          const questions = answer_parsers[type as string](answer as string);
          return [...acc, ...questions];
        }
        if (opts) {
          const parsed_answer = answer_parsers.options(answer as string, opts);
          return [...acc, { question: label as string, answer: parsed_answer }];
        }
        const parsed_answer = json_parser(answer.toString() as string);
        return [...acc, { question: label as string, answer: parsed_answer }];
      },
      [],
    );
    return { id, name, questions };
  }),
);

const Review = ({ id }: IReviewProps) => {
  const sections = useAtomValue(review).filter((section) => section.name !== 'Review & Submit'); // Make sure to remove the review section from the list of sections
  const [checkbox, setCheckbox] = React.useState(false);
  const { addAnswer, setProps } = useProps({ id });

  React.useEffect(() => {
    if (!checkbox) {
      setProps({ required: true });
      addAnswer({ answer: undefined });
      return;
    } // Block submit if checkbox isn't checked
    addAnswer({ answer: 'true' });
    setProps({ required: false });
  }, [checkbox]);

  return (
    <div id={id} className={styles.review}>
      <Text id={`${id}-text`} title={'Review Application'}>
        Please review each section of your application to ensure your information is correct. Once
        you’re ready, click “Submit” to finalize this portion of the application process.
      </Text>
      <div className={styles.sections}>
        {sections.map(({ id: section_id, name, questions }) => (
          <div key={`${id}-${section_id}-review`} className={styles.section.paper}>
            <div className={styles.section.container}>
              <SectionHeader
                id={`${id}-${section_id}-header`}
                section_id={section_id}
                name={name}
              />
              <SectionBody id={`${id}-${section_id}-body`} questions={questions} />
            </div>
          </div>
        ))}
      </div>
      <div className={styles.checkbox}>
        <Checkbox checked={checkbox} onClick={() => setCheckbox(!checkbox)} />
        <span className={styles.checkbox_text}>
          I agree to the Varsity Tutors, LLC &nbsp;
          <a
            className={styles.link}
            href="https://www.varsitytutors.com/terms-of-use"
            target="_blank"
            rel="noreferrer"
          >
            Terms of Use
          </a>{' '}
          &nbsp; and &nbsp;
          <a
            className={styles.link}
            href="https://www.varsitytutors.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          &nbsp; and am 18 years of age or older.
        </span>
      </div>
    </div>
  );
};

export default Review;
